import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Sparkle, X } from '@phosphor-icons/react';

import {
  generateBroadcastDraft,
  uploadBroadcastImage,
  type BroadcastBlock,
} from '../api/broadcasts';
import { ApiError } from '../api/client';
import { Button } from '@/components/ui/button';

// BroadcastAIDialog is the "Create with AI" entry point on the broadcasts
// list. The admin describes the news, optionally chooses a header image
// (AI-picked stock photo OR their own upload), and the dialog asks the
// backend for a proposed subject + body blocks. The result is handed back
// via onGenerated — the caller navigates into the editor with it pre-filled.
//
// The dialog never sends or persists anything: it produces a draft the
// human reviews, edits, and sends through the normal broadcast flow.

// Mirrors the upload handler's allowlist (internal/api/broadcasts.go).
const BROADCAST_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

type ImageMode = 'none' | 'stock' | 'upload';

export interface GeneratedDraft {
  subject: string;
  blocks: BroadcastBlock[];
}

export function BroadcastAIDialog({
  open,
  onClose,
  slug,
  token,
  locale,
  onGenerated,
}: {
  open: boolean;
  onClose: () => void;
  slug: string;
  token: string | null;
  locale: string;
  onGenerated: (draft: GeneratedDraft) => void;
}) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [brief, setBrief] = useState('');
  const [imageMode, setImageMode] = useState<ImageMode>('stock');
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Sync the native <dialog> open state and reset the form each time it
  // opens, so a re-open never shows a stale brief or image.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      setBrief('');
      setImageMode('stock');
      setUploadedKey(null);
      setErrorCode(null);
    }
    if (!open && el.open) el.close();
  }, [open]);

  const busy = generating || uploading;

  async function onPickFile(file: File) {
    setUploading(true);
    setErrorCode(null);
    try {
      const res = await uploadBroadcastImage(token, slug, file);
      setUploadedKey(res.object_key);
      setImageMode('upload');
    } catch {
      setErrorCode('upload_failed');
    } finally {
      setUploading(false);
    }
  }

  async function onGenerate() {
    const trimmed = brief.trim();
    if (!trimmed) {
      setErrorCode('brief_required');
      return;
    }
    setGenerating(true);
    setErrorCode(null);
    try {
      const res = await generateBroadcastDraft(token, slug, {
        brief: trimmed,
        locale,
        want_stock_image: imageMode === 'stock',
      });
      let blocks = res.blocks ?? [];
      // An uploaded header image leads the body, mirroring how the backend
      // prepends a resolved stock photo.
      if (imageMode === 'upload' && uploadedKey) {
        blocks = [{ type: 'image', object_key: uploadedKey }, ...blocks];
      }
      onGenerated({ subject: res.subject, blocks });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ai_unavailable') {
        setErrorCode('ai_unavailable');
      } else if (err instanceof ApiError && err.code === 'invalid_input') {
        setErrorCode('brief_required');
      } else if (err instanceof ApiError && err.code === 'draft_too_long') {
        // The reply got cut off mid-JSON. Retrying the same brief verbatim
        // would fail the same way, so say what to change.
        setErrorCode('too_long');
      } else {
        setErrorCode('unknown');
      }
    } finally {
      setGenerating(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current && !busy) onClose();
      }}
    >
      <div className="flex w-[min(36rem,92vw)] flex-col gap-4 p-6 text-foreground">
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <Sparkle className="mt-0.5 h-6 w-6 text-[var(--lime,#BEE034)]" weight="fill" aria-hidden />
            <div>
              <h2 className="font-heading text-xl font-bold">
                {t('broadcast.ai.title', { defaultValue: 'Create with AI' })}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('broadcast.ai.subtitle', {
                  defaultValue:
                    'Describe the news and AI drafts the subject and content. You review and edit before sending.',
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={onClose}
            disabled={busy}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {t('broadcast.ai.briefLabel', { defaultValue: 'What is the news about?' })}
          </span>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            disabled={busy}
            rows={5}
            maxLength={2000}
            autoFocus
            placeholder={t('broadcast.ai.briefPlaceholder', {
              defaultValue:
                'e.g. Home match against IFK Göteborg this Saturday at 15:00. Free entry for members, café open from 14:00.',
            })}
            className="min-h-[120px] w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <fieldset className="flex flex-col gap-2" disabled={busy}>
          <legend className="mb-1 text-xs font-medium text-muted-foreground">
            {t('broadcast.ai.imageLabel', { defaultValue: 'Header image' })}
          </legend>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <ImageOption
              label={t('broadcast.ai.imageStock', { defaultValue: 'Let AI pick a photo' })}
              checked={imageMode === 'stock'}
              onSelect={() => setImageMode('stock')}
            />
            <ImageOption
              label={t('broadcast.ai.imageUpload', { defaultValue: 'Upload my own' })}
              checked={imageMode === 'upload'}
              onSelect={() => {
                setImageMode('upload');
                if (!uploadedKey) fileRef.current?.click();
              }}
            />
            <ImageOption
              label={t('broadcast.ai.imageNone', { defaultValue: 'No image' })}
              checked={imageMode === 'none'}
              onSelect={() => setImageMode('none')}
            />
          </div>

          {imageMode === 'upload' && (
            <div className="mt-1 flex items-center gap-3">
              {uploadedKey ? (
                <img
                  src={`/storage/${uploadedKey}`}
                  alt=""
                  className="h-16 w-28 rounded-md border border-border object-cover"
                />
              ) : (
                <div className="flex h-16 w-28 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
                  <ImageIcon className="h-6 w-6" weight="duotone" aria-hidden />
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                {uploading
                  ? t('common.submitting', { defaultValue: 'Saving…' })
                  : uploadedKey
                    ? t('imageInput.replace', { defaultValue: 'Replace' })
                    : t('broadcast.ai.chooseImage', { defaultValue: 'Choose image' })}
              </Button>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept={BROADCAST_IMAGE_ACCEPT}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPickFile(f);
              e.target.value = '';
            }}
          />
        </fieldset>

        {errorCode && (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive"
          >
            {t(`broadcast.ai.error.${errorCode}`, {
              defaultValue: t('broadcast.ai.error.unknown', {
                defaultValue: 'Something went wrong. Please try again.',
              }),
            })}
          </p>
        )}

        <footer className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={onGenerate} disabled={busy || !brief.trim()}>
            <Sparkle className="h-4 w-4" weight="bold" aria-hidden />
            {generating
              ? t('broadcast.ai.generating', { defaultValue: 'Generating…' })
              : t('broadcast.ai.generate', { defaultValue: 'Generate draft' })}
          </Button>
        </footer>
      </div>
    </dialog>
  );
}

function ImageOption({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={checked}
      className={`rounded-pill border px-3 py-1.5 text-sm font-medium transition-colors ${
        checked
          ? 'border-foreground bg-foreground text-background'
          : 'border-border bg-background text-foreground hover:bg-muted'
      }`}
    >
      {label}
    </button>
  );
}
